import { z } from "zod";

/** UUIDv4 identifiers are used for every client-generated id. */
export const Uuid = z.uuid();
export const DeviceId = Uuid;
export const VaultId = Uuid;
export const ProjectId = Uuid;
export const FileId = Uuid;
export const RuleId = Uuid;
export const OpId = Uuid;
export const AccountId = Uuid;

export const Base64 = z.base64();
/** base64(24-byte nonce || 32-byte key || 16-byte tag) is always exactly 96 characters. */
export const WrapCiphertext = z.base64().length(96);

/** Relative paths joined under a project directory: no absolute prefix, `..`, NUL or backslash. */
export const SafeRelPath = z
  .string()
  .min(1)
  .max(4096)
  .refine(
    (p) =>
      !p.startsWith("/") &&
      !p.includes("\0") &&
      !p.includes("\\") &&
      p.split("/").every((c) => c !== "" && c !== "." && c !== ".."),
    "unsafe path",
  );
/** Device-local project paths are home-relative. */
export const SafeLocalPath = z
  .string()
  .max(4096)
  .refine(
    (p) => p === "~" || (p.startsWith("~/") && SafeRelPath.safeParse(p.slice(2)).success),
    "path must be under ~",
  );
/** dotenv key charset. */
export const EnvKeyName = z.string().regex(/^[A-Za-z_][A-Za-z0-9_.-]{0,1023}$/);
/** Lowercase hex BLAKE3-256 digest. */
export const Hash256 = z.string().regex(/^[0-9a-f]{64}$/);

/** Hybrid logical clock: total order by (wall_ms, counter, device_id). */
export const Hlc = z.object({
  wall_ms: z.int().nonnegative(),
  counter: z.int().nonnegative(),
  device_id: DeviceId,
});
export type Hlc = z.infer<typeof Hlc>;

export function compareHlc(a: Hlc, b: Hlc): -1 | 0 | 1 {
  if (a.wall_ms !== b.wall_ms) return a.wall_ms < b.wall_ms ? -1 : 1;
  if (a.counter !== b.counter) return a.counter < b.counter ? -1 : 1;
  if (a.device_id !== b.device_id) return a.device_id < b.device_id ? -1 : 1;
  return 0;
}

export const FileMode = z.enum(["sync", "backup", "inventory"]);
export type FileMode = z.infer<typeof FileMode>;

export const FileKind = z.enum(["env", "blob"]);
export type FileKind = z.infer<typeof FileKind>;

export const Platform = z.enum(["macos", "linux", "windows", "browser"]);
export type Platform = z.infer<typeof Platform>;
